import { useEffect, useState } from "react";
import { Link } from "react-router";
import { BrandMark } from "@mattfinkel/common-web";
import { appCatalog, groupCatalog, type AppCatalogEntry, type AppCatalogGroup } from "./catalog";
import { applyCatalogOrder, defaultCatalogOrder, moveCatalogEntry, readCatalogOrder, writeCatalogOrder } from "./order";
import { prewarmApp } from "./prewarm";

export function AppDirectory({
  groups,
  reordering = false,
  onMove,
}: {
  groups: AppCatalogGroup[];
  reordering?: boolean;
  onMove?(app: AppCatalogEntry, direction: -1 | 1): void;
}) {
  return (
    <section aria-label="Apps">
      <div className="space-y-9">
        {groups.map((group) => (
          <section key={group.id} aria-labelledby={`group-${group.id}`}>
            <h2 id={`group-${group.id}`} className="mb-3 text-xs font-semibold tracking-wide text-ink-muted uppercase">
              {group.label}
            </h2>
            <div className="grid grid-cols-2 gap-x-4 gap-y-6 sm:grid-cols-4">
              {group.apps.map((app, index) => (
                <AppTile
                  key={app.id}
                  app={app}
                  reordering={reordering}
                  canMoveEarlier={index > 0}
                  canMoveLater={index < group.apps.length - 1}
                  onMove={onMove}
                />
              ))}
            </div>
          </section>
        ))}
      </div>
    </section>
  );
}

function TileContents({ app }: { app: AppCatalogEntry }) {
  const markClassName = "mx-auto aspect-square w-full max-w-28 rounded-[22%] shadow-lg shadow-black/25 transition duration-200 group-hover:-translate-y-1 group-hover:shadow-xl sm:max-w-32";

  return (
    <>
      {app.brand ? (
        <BrandMark brand={app.brand} alt="" className={markClassName} />
      ) : (
        <img src={app.icon} alt="" className={markClassName} />
      )}
      <span className="mt-2.5 block truncate text-sm font-semibold text-ink">{app.name}</span>
      <span className="mt-0.5 hidden text-xs text-ink-muted sm:block">{app.description}</span>
    </>
  );
}

export function AppTile({
  app,
  reordering = false,
  canMoveEarlier = false,
  canMoveLater = false,
  onMove,
}: {
  app: AppCatalogEntry;
  reordering?: boolean;
  canMoveEarlier?: boolean;
  canMoveLater?: boolean;
  onMove?(app: AppCatalogEntry, direction: -1 | 1): void;
}) {
  const className = "group min-w-0 text-center";
  if (reordering) {
    return (
      <div className={className}>
        <TileContents app={app} />
        <div className="mx-auto mt-2 flex max-w-28 justify-center gap-2 sm:max-w-32">
          <button
            type="button"
            disabled={!canMoveEarlier}
            aria-label={`Move ${app.name} earlier`}
            onClick={() => onMove?.(app, -1)}
            className="grid h-8 flex-1 place-items-center rounded-lg border border-border-subtle text-sm text-ink-muted hover:border-ink-muted hover:text-ink disabled:opacity-25"
          >
            ←
          </button>
          <button
            type="button"
            disabled={!canMoveLater}
            aria-label={`Move ${app.name} later`}
            onClick={() => onMove?.(app, 1)}
            className="grid h-8 flex-1 place-items-center rounded-lg border border-border-subtle text-sm text-ink-muted hover:border-ink-muted hover:text-ink disabled:opacity-25"
          >
            →
          </button>
        </div>
      </div>
    );
  }
  if (!app.external) {
    return <Link to={app.href} className={className}><TileContents app={app} /></Link>;
  }
  return (
    <a
      href={app.href}
      target="_blank"
      rel="noreferrer"
      className={className}
      onFocus={() => prewarmApp(app)}
      onPointerEnter={() => prewarmApp(app)}
      onTouchStart={() => prewarmApp(app)}
    >
      <TileContents app={app} />
    </a>
  );
}

export default function Launchpad() {
  const [reordering, setReordering] = useState(false);
  const [order, setOrder] = useState(() => readCatalogOrder(window.localStorage, appCatalog));

  useEffect(() => {
    appCatalog.filter((app) => app.group === "daily").forEach(prewarmApp);
    const rest = window.setTimeout(() => {
      appCatalog.filter((app) => app.group !== "daily").forEach(prewarmApp);
    }, 1500);
    return () => window.clearTimeout(rest);
  }, []);

  const updateOrder = (next: ReturnType<typeof defaultCatalogOrder>) => {
    setOrder(next);
    writeCatalogOrder(window.localStorage, next);
  };

  return (
    <div data-testid="launchpad" className="-mt-8 pb-6">
      <div
        data-testid="reorder-controls"
        className="fixed right-[calc(1rem+env(safe-area-inset-right))] bottom-[calc(1rem+env(safe-area-inset-bottom))] z-20 flex items-center gap-3 rounded-2xl border border-border-subtle bg-surface-raised/95 p-2 shadow-xl backdrop-blur"
      >
        {reordering && (
          <button type="button" onClick={() => updateOrder(defaultCatalogOrder(appCatalog))} className="text-xs text-ink-muted underline underline-offset-4 hover:text-ink">
            Reset order
          </button>
        )}
        <button type="button" onClick={() => setReordering((current) => !current)} className="rounded-xl border border-border-subtle px-3 py-1.5 text-xs font-semibold text-ink-muted hover:border-ink-muted hover:text-ink">
          {reordering ? "Done" : "Reorder"}
        </button>
      </div>
      {reordering && <p className="mb-5 text-right text-xs text-ink-muted">Order is saved on this device.</p>}
      <AppDirectory
        groups={groupCatalog(applyCatalogOrder(appCatalog, order))}
        reordering={reordering}
        onMove={(app, direction) => updateOrder(moveCatalogEntry(order, app, direction))}
      />
    </div>
  );
}
